/**
 * Parent → Students lookup
 *
 * Resolves the logged-in parent's linked students via:
 *   auth.uid → users.id → parents.user_id → parents.id
 *   → student_parents.parent_id → students.id → users (names)
 *
 * Returned rows match the shape the parent dashboard DataTable expects
 * (attributes.first_name, etc.) so the existing DataTable code keeps working.
 *
 * The metric maths (progress %, current-period average, GPA band, last access)
 * lives in services/shared/studentMetrics.ts — the teacher dashboard runs the
 * SAME engine, so a child can never read one average on the parent screen and a
 * different one on the teacher screen. `computeChildMetrics` and the row type
 * are re-exported below so every existing `services/parent/students` import
 * keeps working unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase";
import { NOT_ARCHIVED_SUBJECT_ENROLMENT } from "../shared/archivedSubjectCourses";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";
import { computeChildMetrics, type StudentMetricsRow } from "../shared/studentMetrics";

// Re-export shim — see the module comment above.
export { computeChildMetrics } from "../shared/studentMetrics";

/** The parent roster row. Identical to the shared envelope; kept as a named
 *  alias because parent-side call sites import it under this name. */
export type ParentStudentRow = StudentMetricsRow;

// ---------------------------------------------------------------------------
// Parent's children's enrolled courses
// ---------------------------------------------------------------------------
// Joins: parents.user_id → parents → student_parents → course_enrollments
//        → courses → subjects
// Returned shape matches what CourseCardsTemplate expects (x.attributes.*).

export interface ParentCourseRow {
  id: string;
  student_id: string;
  course_title: string;
  attributes: {
    course_title: string;
    subject: { color: string | null; icon: string | null };
    school_year: { end_date: string | null } | null;
  };
}

// W2 dashboard-progress fix: the per (student, course) lesson tally that used
// to live here (`computeLessonTallies` — "every activity in the lesson
// answered") was a FOURTH, independent definition of "course progress",
// disagreeing with the Gradebook bar, the PT/teacher cards, AND the parent
// Dashboard's own (also broken) numerator. Removed. Progress is now merged
// onto these rows by the render site (parentWorkSpace/students/Courses.tsx)
// via useCourseProgressBatch(studentIds) — the same computeCourseProgress
// engine every other card now reads — using the `studentIds` this hook
// returns below.

export function useGetParentStudentCourses() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<ParentCourseRow[]>([]);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: parent, error: parentErr } = await supabase
        .from("parents")
        .select("id")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (parentErr) throw parentErr;
      if (!parent) {
        setRows([]);
        return;
      }

      // Get linked student ids
      const { data: links, error: linkErr } = await supabase
        .from("student_parents")
        .select("student_id")
        .eq("parent_id", parent.id);
      if (linkErr) throw linkErr;
      const linkedStudentIds = (links ?? []).map((r) => r.student_id).filter(Boolean);
      setStudentIds(linkedStudentIds);
      if (linkedStudentIds.length === 0) {
        setRows([]);
        return;
      }

      // Enrollments + course + subject
      const { data: enrollments, error: enrollErr } = await supabase
        .from("course_enrollments")
        .select(
          "id, student_id, courses(id, course_title, subjects(color, icon))",
        )
        .in("student_id", linkedStudentIds)
        .or(NOT_ARCHIVED_SUBJECT_ENROLMENT);
      if (enrollErr) throw enrollErr;

      const mapped: ParentCourseRow[] = (enrollments ?? [])
        .map((e): ParentCourseRow | null => {
          // `courses` and the nested `subjects` are to-one embeds; PostgREST
          // may type each as an array, so normalize before reading.
          const course = Array.isArray(e.courses) ? e.courses[0] : e.courses;
          if (!course) return null;
          const subject = Array.isArray(course.subjects) ? course.subjects[0] : course.subjects;
          return {
            id: course.id,
            student_id: e.student_id,
            course_title: course.course_title ?? "Untitled",
            attributes: {
              course_title: course.course_title ?? "Untitled",
              subject: {
                color: subject?.color ?? null,
                icon: subject?.icon ?? null,
              },
              school_year: null, // TODO: join to cycles/school_years when needed
            },
          };
        })
        .filter((row): row is ParentCourseRow => row !== null);

      setRows(mapped);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load courses'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // `studentIds` is exposed so the render site can batch-fetch real progress
  // via useCourseProgressBatch(studentIds) and merge it onto `rows` by
  // (student_id, id) — see the module comment above.
  return { rows, studentIds, loading, error, refetch: fetchCourses };
}

// ---------------------------------------------------------------------------
// Flat lookup shape — for parent-side dropdowns and list pages that don't
// need the DataTable-flavored `attributes.*` envelope. The list pages used
// to call the generic `useGetStudentsApi({})` from services/shared/student
// which returned every student in the org (RLS is org-scoped only). Use
// this hook instead so parents only see their own children.
// ---------------------------------------------------------------------------

export interface ParentChildLookup {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  grade_level: string | null;
}

export function useGetMyChildrenLookup() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<ParentChildLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChildren = useCallback(async () => {
    if (!profile?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: parent, error: pErr } = await supabase
        .from("parents")
        .select("id")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (pErr) throw pErr;
      if (!parent) {
        setRows([]);
        return;
      }
      const { data, error: sErr } = await supabase
        .from("student_parents")
        .select("students(id, first_name, last_name, email, student_grade_levels(active, grade_level:grade_levels(grade_level)))")
        .eq("parent_id", (parent as { id: string }).id);
      if (sErr) throw sErr;
      const mapped: ParentChildLookup[] = (data ?? [])
        // `students` is a to-one embed; PostgREST may type it as an array.
        .map((r) => (Array.isArray(r.students) ? r.students[0] : r.students))
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
        .map((s) => ({
          id: s.id,
          first_name: s.first_name ?? null,
          last_name: s.last_name ?? null,
          email: s.email ?? null,
          grade_level: s.student_grade_levels?.find((g: { active: boolean | null }) => g.active)?.grade_level?.grade_level ?? null,
        }));
      setRows(mapped);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load children'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  return { rows, loading, error, refetch: fetchChildren };
}

export async function fetchParentStudents(profileId: string): Promise<ParentStudentRow[]> {
  const { data: parent, error: parentErr } = await supabase
    .from("parents")
    .select("id")
    .eq("user_id", profileId)
    .maybeSingle();
  if (parentErr) throw parentErr;
  if (!parent) return [];

  const { data, error: studentErr } = await supabase
    .from("student_parents")
    .select(
      "students(id, first_name, last_name, email, user_name, users!students_user_id_fkey(status))",
    )
    .eq("parent_id", parent.id);
  if (studentErr) throw studentErr;

  const students = (data ?? [])
    .map((row) => (Array.isArray(row.students) ? row.students[0] : row.students))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const metrics = await computeChildMetrics(students.map((s) => s.id));

  return students.map((s) => {
    const m = metrics.get(s.id) ?? { progressCompleted: null, semesterAvg: null, periodAvgs: {}, gpa: null, lastAccess: null };
    const user = Array.isArray(s.users) ? s.users[0] : s.users;
    return {
      id: s.id,
      type: "student_progress_data",
      attributes: {
        first_name: s.first_name ?? "",
        last_name: s.last_name ?? "",
        email: s.email ?? null,
        user_name: s.user_name ?? null,
        status: user?.status ?? null,
        progress: { completed: m.progressCompleted },
        semsester_avg: m.semesterAvg,
        period_avgs: m.periodAvgs,
        gpa: m.gpa,
        last_access: m.lastAccess,
      },
    };
  });
}

export function useParentStudentsQuery() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;

  return useQuery({
    queryKey: ["parent-students", profileId],
    queryFn: () => fetchParentStudents(profileId as string),
    enabled: profileId != null,
  });
}

export function useGetParentStudents() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<ParentStudentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStudents = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchParentStudents(profile.id));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load students'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { rows, loading, error, refetch: fetchStudents };
}
